import { useState } from 'react';
import styled from 'styled-components';
import { Headphones, SignOut, Storefront } from '@phosphor-icons/react';
import { DarkPopup, OrganisationAvatar } from '../components';
import { useAuth } from '../contexts/auth';
import { AccountService } from '../utility/services/account';
import { BrowserUtility, theme, ToastMessage, UserService } from '../utility';
import { MuteText } from '../elements';
import { goBackToPortal, portalLoginUrl } from '../screens/unified-login.helpers.js';

const OrganisationContainer = styled.div`
  display: flex;
  column-gap: 10px;
  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
  }
  border-radius: 5px;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryLight};
  }

  &.active {
    background: ${({ theme }) => theme.colors.primaryLight};
  }

  padding: 7px;
  cursor: pointer;
`;

const EmailContainer = styled.div`
  flex: 1;
  margin-left: 10px;
`;

const OrganisationList = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 3px;
  margin-top: 5px;
  max-height: 30vh;
  overflow-y: auto;
`;

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primaryLight};
  width: 30px;
  height: 30px;
  border-radius: 5px;
  cursor: pointer;
`;

const HeaderButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  border-radius: 5px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 5px;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const UNIFIED_LOGIN_ENV = {
  VITE_UNIFIED_LOGIN: import.meta.env.VITE_UNIFIED_LOGIN,
  VITE_PORTAL_URL: import.meta.env.VITE_PORTAL_URL,
};
// Same-tab link to the portal login with ?app=portal; null hides the item (unified login off).
const PORTAL_LOGIN_URL = portalLoginUrl(UNIFIED_LOGIN_ENV);

export function UserOrgMenu() {
  const [orgLoading, setOrgLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { logout: providerLogout, currentOrg, user, setUserData } = useAuth();

  const logoutClick = () => {
    providerLogout();
  };

  const changeOrg = async item => {
    try {
      setOrgLoading(true);
      const response = await AccountService.switchOrganization(item._id);
      BrowserUtility.removeAll();
      setTimeout(() => {
        UserService.storeUser(response);
        setUserData(response);
        window.location.reload();
      }, 100);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setOrgLoading(false);
    }
  };

  // EN-4104: one click into the portal through a one-time code; the link's own href (the plain
  // portal login) is the fallback, and stays what a new-tab click opens.
  const backToPortal = event => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    if (portalLoading) return;
    setPortalLoading(true);
    void goBackToPortal({
      env: UNIFIED_LOGIN_ENV,
      requestCode: UserService.portalHandoff,
      assign: url => window.location.assign(url),
    });
  };

  return (
    <DarkPopup
      trigger={
        <AvatarContainer>
          <OrganisationAvatar name={user.firstName} mini />
        </AvatarContainer>
      }
      hoverable
      position="bottom right"
      basic
      offset={[0, -5]}
      contentMaxHight="80vh"
    >
      <div className="d-flex mb-2">
        <OrganisationAvatar name={user.firstName} />
        <EmailContainer className="d-flex flex-column">
          <span>{user.firstName}</span>
          <MuteText>{user.email}</MuteText>
        </EmailContainer>
      </div>
      <b>Organizations</b>
      <OrganisationList>
        {user.organizations.map(org => (
          <OrganisationContainer
            className={org._id === currentOrg._id ? 'active' : ''}
            onClick={() => {
              if (!orgLoading && org._id !== currentOrg._id) {
                changeOrg(org);
              }
            }}
            key={org._id}
            disabled={orgLoading}
          >
            <OrganisationAvatar name={org.name} mini />
            <span className="name">{org.name}</span>
          </OrganisationContainer>
        ))}
      </OrganisationList>
      <a
        href="https://skupreme.atlassian.net/helpcenter/POR/user/requests"
        target="_blank"
        rel="noreferrer"
      >
        <HeaderButton className="w-100 mt-2">
          <Headphones className="mr-1" size={20} />
          Support Portal
        </HeaderButton>
      </a>
      {PORTAL_LOGIN_URL && (
        <a href={PORTAL_LOGIN_URL} onClick={backToPortal} aria-busy={portalLoading}>
          <HeaderButton>
            <Storefront className="mr-1" size={20} />
            Back to portal
          </HeaderButton>
        </a>
      )}
      <HeaderButton onClick={logoutClick}>
        <SignOut className="mr-1" size={20} />
        Sign Out
      </HeaderButton>
    </DarkPopup>
  );
}
